import React from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { useWirelessAdvisor } from '../../context/WirelessAdvisorContext';

/**
 * StepIndicator component
 * Shows the progress through the form steps
 * 
 * @param {Object} props - Component props
 * @param {Array} props.steps - Array of step names
 * @param {string} props.currentStep - Current step name
 * @param {Function} props.onStepClick - Function to call when a step is clicked
 * @returns {JSX.Element} StepIndicator component
 */
const StepIndicator = ({ 
  steps, 
  currentStep, 
  onStepClick = () => {} 
}) => {
  const { isStepComplete } = useWirelessAdvisor();
  
  // Get step label
  const getStepLabel = (step) => {
    switch (step) {
      case 'size':
        return 'Home Size';
      case 'routerLocation':
        return 'Router Location';
      case 'rooms':
        return 'Rooms';
      case 'layout':
        return 'Layout';
      case 'coverage':
        return 'Coverage';
      case 'devices':
        return 'Devices';
      case 'usage':
        return 'Usage';
      default:
        return step.charAt(0).toUpperCase() + step.slice(1);
    }
  };
  
  // Get step status
  const getStepStatus = (step) => {
    const currentStepIndex = steps.indexOf(currentStep);
    const stepIndex = steps.indexOf(step);
    
    if (stepIndex < currentStepIndex && isStepComplete(step)) {
      return 'completed';
    } else if (step === currentStep) {
      return 'current';
    } else if (stepIndex < currentStepIndex) {
      return 'incomplete';
    } else {
      return 'upcoming';
    }
  };
  
  // Get step classes
  const getStepClasses = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500 text-white border-green-500';
      case 'current':
        return 'bg-purple-600 text-white border-purple-600';
      case 'incomplete':
        return 'bg-gray-300 text-gray-600 border-gray-300';
      case 'upcoming':
        return 'bg-gray-200 text-gray-400 border-gray-200';
      default:
        return 'bg-gray-200 text-gray-400 border-gray-200';
    }
  };
  
  // Get connector classes
  const getConnectorClasses = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500';
      case 'current':
        return 'bg-purple-600';
      case 'incomplete':
      case 'upcoming':
      default:
        return 'bg-gray-200';
    }
  };
  
  return (
    <div className="flex items-center w-full">
      {steps.map((step, index) => {
        const status = getStepStatus(step);
        const isClickable = status === 'completed' || status === 'current';
        
        return (
          <React.Fragment key={step}>
            {/* Step Circle */}
            <motion.div
              className={`relative flex items-center justify-center w-8 h-8 rounded-full border-2 ${getStepClasses(status)} ${isClickable ? 'cursor-pointer' : 'cursor-default'}`}
              onClick={() => isClickable && onStepClick(step)}
              whileHover={isClickable ? { scale: 1.1 } : {}}
              whileTap={isClickable ? { scale: 0.95 } : {}}
            >
              {status === 'completed' ? (
                <Check className="w-4 h-4" />
              ) : (
                <span className="text-xs font-medium">{index + 1}</span>
              )}
              
              {/* Step Label (visible on larger screens) */}
              <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 whitespace-nowrap text-xs font-medium hidden md:block">
                {getStepLabel(step)}
              </div>
            </motion.div>
            
            {/* Connector Line */}
            {index < steps.length - 1 && (
              <div className="flex-1 h-0.5 mx-1">
                <div className={`h-full ${getConnectorClasses(status)}`}></div>
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default StepIndicator;